'use client';

import type { PropsWithChildren } from 'react';
import { isNull } from 'lodash';
import type {
  InboxAccount,
  UserAccountData,
} from '@frameio/graphql-client/generated';
import {
  useGetAccountsForRoutingQuery,
  useMeQuery,
} from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import type { DeepPartial } from '@frameio/utils';
import { useMigrationStartedAt } from '../../account-migration/hooks/useMigrationStartedAt';
import { useActiveAccountId } from '../ActiveEntitiesContext';
import { IsolatedActiveAccountIdContextProvider } from '../ActiveEntitiesContext/ActiveAccountIdContext';
import { useSignupFromQueryParam } from './useSignupFromQueryParam';

type Account = DeepPartial<UserAccountData['account']>;

export const useDefaultAccountId = ({
  filter,
  skip,
}: {
  filter?: (account: Account | InboxAccount) => boolean;
  skip: boolean;
}): string | null | undefined => {
  const signup = useSignupFromQueryParam();
  const response = useGetAccountsForRoutingQuery({
    variables: {
      first: 500,
      signup,
    },
    skip,
  });

  if (!response.data?.me) return undefined;

  // User has no accounts, this happens if they are merely an IdentifiedUser
  if (!('accounts' in response.data.me)) return null;

  const allAccounts = response.data.me.accounts.nodes;

  // If passed a filter function, run it
  const filteredAccounts = filter
    ? allAccounts.filter(({ account }) => filter(account))
    : allAccounts;

  // If there are no accounts after filtering, return null
  if (!filteredAccounts.length) {
    const inboxAccounts = response.data.me?.inboxAccounts?.nodes.filter(
      (account) => filter?.(account)
    );

    return inboxAccounts?.[0]?.id || null;
  }

  // Return the first account ID
  return filteredAccounts[0].account.id;
};

/**
 * Determines the account ID that the current user is the owner of.
 * @param skip - Whether to skip the query
 * @param userId - The user ID
 * @returns The account ID that the current user is the owner of, undefined if the query is loading or null if the user has no accounts.
 */
const useOwnedAccount = ({
  skip,
  userId,
}: {
  skip: boolean;
  userId?: string;
}) => {
  const signup = useSignupFromQueryParam();
  const response = useGetAccountsForRoutingQuery({
    variables: {
      first: 100,
      signup,
    },
    skip,
  });

  if (!response.data?.me) return undefined;

  // User has no accounts, this happens if they are merely an IdentifiedUser
  if (!('accounts' in response.data.me)) return null;

  // `Me` query has not resolved yet
  if (!userId) return undefined;

  const allAccounts = response.data.me.accounts.nodes;

  // Find the first account owned by the user
  const ownedAccount = allAccounts.find(
    ({ account }) => account.owner?.id === userId
  );

  return ownedAccount?.account || null;
};

/**
 * Returns the ID of the account owned by the current user when that account is:
 * - a V3 account (version < 4)
 * - currently migrating (i.e. has `migrationStartedAt`)
 * @param skip - Whether to skip the query
 * @param userId - The user ID
 * @returns The migrating owned account ID, `null` when none exists, or `undefined` while data is loading.
 */
const useMigratingOwnedAccountId = ({
  skip,
  userId,
}: {
  skip: boolean;
  userId?: string;
}) => {
  const ownedAccount = useOwnedAccount({ skip, userId });
  const migrationStartedAt = useMigrationStartedAt({
    skip: skip || !ownedAccount,
    accountId: ownedAccount?.id,
  });

  // user has no owned accounts
  if (ownedAccount === null) return null;
  // queries are loading
  if (typeof ownedAccount === 'undefined') return undefined;
  if (typeof migrationStartedAt === 'undefined') return undefined;

  // user owns a migrating V3 account
  if (
    ownedAccount &&
    'id' in ownedAccount &&
    ownedAccount.version &&
    ownedAccount.version < 4 &&
    migrationStartedAt
  )
    return ownedAccount.id;

  return null;
};

const useABPAccount = ({
  skip,
  userId,
}: {
  skip: boolean;
  userId?: string;
}) => {
  const signup = useSignupFromQueryParam();
  const response = useGetAccountsForRoutingQuery({
    variables: {
      first: 100,
      signup,
    },
    skip,
  });

  if (!response.data?.me) return undefined;

  // User has no accounts, this happens if they are merely an IdentifiedUser
  if (!('accounts' in response.data.me)) return null;

  // `Me` query has not resolved yet
  if (!userId) return undefined;

  const allAccounts = response.data.me.accounts.nodes;

  // Find the first ABP acccount
  const abpAccount = allAccounts.find(({ account }) =>
    isAccountAdobeManaged(account.managedBy)
  );

  return abpAccount?.account || null;
};

export const DefaultAccountId = ({ children }: PropsWithChildren) => {
  const activeAccountId = useActiveAccountId();

  const { data: user } = useMeQuery();

  const userId = user?.me?.id;
  const skip = Boolean(activeAccountId);

  const defaultV4AccountId = useDefaultAccountId({
    skip,
    filter: (account) => account.version === 4,
  });
  const defaultV3AccountId = useDefaultAccountId({
    skip,
    filter: (account) => account.version === 3,
  });
  const migratingOwnedV3AccountId = useMigratingOwnedAccountId({
    skip,
    userId,
  });

  const abpAccount = useABPAccount({ skip, userId });

  const defaultAccountId =
    // First ensure all the above queries are settled
    typeof defaultV4AccountId !== 'undefined' &&
    typeof defaultV3AccountId !== 'undefined' &&
    typeof migratingOwnedV3AccountId !== 'undefined' &&
    (migratingOwnedV3AccountId || defaultV4AccountId || defaultV3AccountId);

  const accountId = isNull(defaultV3AccountId)
    ? activeAccountId || defaultV4AccountId
    : activeAccountId || defaultAccountId;

  const resolvedAccountId =
    accountId === null && abpAccount?.id === undefined
      ? null
      : accountId || abpAccount?.id;

  return (
    <IsolatedActiveAccountIdContextProvider
      accountId={resolvedAccountId as string}
    >
      {children}
    </IsolatedActiveAccountIdContextProvider>
  );
};
